import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X, ExternalLink, Copy, Share, Trash2, Calendar, Layers, Folder, Rss } from 'lucide-react';

const PHONE_SCREEN_ID = 'phone-screen';

const PROCESSING_LEVEL_LABELS = {
  summary:    'Summary',
  key_points: 'Key Points',
  detailed:   'Detailed',
  full:       'Full',
};

const pill = {
  background: 'rgba(0,0,0,0.05)',
  border: '1px solid rgba(0,0,0,0.10)',
  color: 'rgba(0,0,0,0.60)',
};

const ClipDetailModal = ({ clip, isOpen, onClose, onDelete, onUpdateClip, folders }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [copied, setCopied] = useState(false);

  const feedStatus = clip?.feedStatus ?? 'not_in_feed';
  const processingLevel = clip?.processingLevel ?? 'summary';

  useEffect(() => {
    if (isOpen) requestAnimationFrame(() => setIsAnimating(true));
    else setIsAnimating(false);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  const handleCopyLink = useCallback(async () => {
    if (clip?.url) {
      try {
        await navigator.clipboard.writeText(clip.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }, [clip?.url]);

  const handleOpenLink = useCallback(() => {
    if (clip?.url) window.open(clip.url, '_blank');
  }, [clip?.url]);

  const handleShare = useCallback(async () => {
    if (navigator.share && clip) {
      try {
        await navigator.share({ title: clip.title, url: clip.url || undefined });
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Share failed:', err);
      }
    }
  }, [clip]);

  const handleDelete = useCallback(() => {
    if (clip) { onDelete(clip.id); handleClose(); }
  }, [clip, onDelete, handleClose]);

  const handleFeedStatusChange = useCallback((value) => {
    if (clip && onUpdateClip) onUpdateClip(clip.id, { feedStatus: value });
  }, [clip, onUpdateClip]);

  const handleProcessingLevelChange = useCallback((value) => {
    if (clip && onUpdateClip) onUpdateClip(clip.id, { processingLevel: value });
  }, [clip, onUpdateClip]);

  const handleFolderChange = useCallback((folderId) => {
    if (clip && onUpdateClip) onUpdateClip(clip.id, { folderId });
  }, [clip, onUpdateClip]);

  const getTypeIcon = (type) => ({
    article: '📄', person: '👤', movie: '🎬', topic: '💡',
    image: '🖼️', video: '🎥', audio: '🎵', file: '📁', link: '🔗',
  }[type] || '📋');

  if (!isOpen || !clip) return null;

  const modalContent = (
    <div
      className="absolute inset-0 z-[100] flex flex-col justify-end transition-all duration-300"
      style={{ background: isAnimating ? 'rgba(0,0,0,0.30)' : 'transparent' }}
      onClick={handleBackdropClick}
    >
      <div
        className="w-full flex flex-col overflow-hidden transition-all duration-300 ease-out"
        style={{
          backgroundColor: '#F8F5EE',
          borderRadius: '20px 20px 0 0',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 -4px 16px rgba(0,0,0,0.10)',
          height: 'calc(100% - 2.5rem)',
          transform: isAnimating ? 'translateY(0)' : 'translateY(100%)',
          opacity: isAnimating ? 1 : 0,
        }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-9 h-1 rounded-full" style={{ background: 'rgba(0,0,0,0.15)' }} />
        </div>

        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.07)' }}
        >
          <div className="flex items-center gap-2.5">
            <span className="text-2xl">{getTypeIcon(clip.type)}</span>
            <span
              className="px-2.5 py-1 rounded-full text-[9px] uppercase font-semibold"
              style={{ letterSpacing: '1.5px', background: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.55)' }}
            >
              {clip.type}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.08)' }}
            aria-label="Close"
          >
            <X className="w-4 h-4" style={{ color: 'rgba(0,0,0,0.55)' }} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="px-5 pb-8 overflow-y-auto flex-1 pt-4">
          {clip.fileData && (
            <div className="mb-4 rounded-[12px] overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.08)' }}>
              {clip.type === 'image' && (
                <img src={clip.fileData} alt={clip.title} className="w-full max-h-56 object-contain" />
              )}
              {clip.type === 'video' && (
                <video src={clip.fileData} className="w-full max-h-56" controls preload="metadata" />
              )}
              {clip.type === 'audio' && (
                <div className="p-4 bg-[#ECE7DC]">
                  <div className="text-4xl mb-3 text-center">🎵</div>
                  <audio src={clip.fileData} className="w-full" controls preload="metadata" />
                </div>
              )}
            </div>
          )}

          {/* Title + meta */}
          <div className="pb-4 mb-4" style={{ borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
            <h2 className="font-instrument text-[32px] leading-[1.1] text-[#111] mb-2">{clip.title}</h2>
            <div className="flex flex-wrap gap-3 text-[10px] uppercase font-semibold" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.40)' }}>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Added {clip.addedAt}
              </span>
            </div>
          </div>

          {/* Source URL */}
          {clip.url && (
            <div
              className="mb-4 p-3 rounded-[10px]"
              style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)' }}
            >
              <p className="text-[10px] uppercase font-semibold mb-1 flex items-center gap-1" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.40)' }}>
                <ExternalLink className="w-3 h-3" /> Source
              </p>
              <p className="text-[14px] truncate" style={{ color: 'rgba(0,0,0,0.65)' }}>{clip.url}</p>
            </div>
          )}

          {onUpdateClip && (
            <>
              {/* Organize */}
              {Array.isArray(folders) && folders.length > 0 && (
                <div className="mb-4">
                  <p className="text-[10px] uppercase font-semibold mb-2 flex items-center gap-1.5" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.40)' }}>
                    <Folder className="w-3.5 h-3.5" /> Organize
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {folders.map((folder) => (
                      <button
                        key={folder.id}
                        type="button"
                        onClick={() => handleFolderChange(folder.id)}
                        className="px-3 py-1.5 rounded-full text-[10px] uppercase font-semibold transition-all"
                        style={{
                          letterSpacing: '1.5px',
                          ...(clip.folderId === folder.id
                            ? { background: '#064e3b', color: '#fff', border: '1px solid #064e3b' }
                            : pill),
                        }}
                      >
                        {folder.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Processing level */}
              <div className="mb-4">
                <p className="text-[10px] uppercase font-semibold mb-2 flex items-center gap-1.5" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.40)' }}>
                  <Layers className="w-3.5 h-3.5" /> Processing Level
                </p>
                <div className="flex flex-wrap gap-2">
                  {['summary', 'key_points', 'detailed', 'full'].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleProcessingLevelChange(value)}
                      className="px-3 py-1.5 rounded-full text-[10px] uppercase font-semibold transition-all"
                      style={{
                        letterSpacing: '1.5px',
                        ...(processingLevel === value
                          ? { background: '#064e3b', color: '#fff', border: '1px solid #064e3b' }
                          : pill),
                      }}
                    >
                      {PROCESSING_LEVEL_LABELS[value]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Feed status */}
              <div className="mb-5 pt-4" style={{ borderTop: '1px solid rgba(0,0,0,0.07)' }}>
                <p className="text-[10px] uppercase font-semibold mb-2 flex items-center gap-1.5" style={{ letterSpacing: '1.5px', color: 'rgba(0,0,0,0.40)' }}>
                  <Rss className="w-3.5 h-3.5" /> Feed
                </p>
                {feedStatus === 'in_feed' ? (
                  <div
                    className="flex items-center justify-between rounded-[10px] px-3 py-2.5"
                    style={{ background: 'rgba(6,78,59,0.08)', border: '1px solid rgba(6,78,59,0.15)' }}
                  >
                    <span className="text-[11px] uppercase font-semibold flex items-center gap-1.5" style={{ letterSpacing: '1px', color: '#064e3b' }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#064e3b]" /> In Feed
                    </span>
                    <button
                      type="button"
                      onClick={() => handleFeedStatusChange('not_in_feed')}
                      className="text-[11px] uppercase font-semibold"
                      style={{ letterSpacing: '1px', color: 'rgba(0,0,0,0.45)' }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleFeedStatusChange('in_feed')}
                    className="w-full py-2.5 px-3 rounded-[20px] text-white text-[11px] uppercase font-semibold bg-[#064e3b] transition-all active:scale-[0.97]"
                    style={{ letterSpacing: '1.5px', boxShadow: '0 3px 8px rgba(6,78,59,0.30)' }}
                  >
                    Add to Feed
                  </button>
                )}
              </div>
            </>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 flex-wrap">
            {clip.url && (
              <>
                <button
                  onClick={handleOpenLink}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[12px] text-white bg-[#064e3b] text-[11px] uppercase font-semibold transition-all active:scale-[0.97]"
                  style={{ letterSpacing: '1px', minWidth: '80px' }}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open
                </button>
                <button
                  onClick={handleCopyLink}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[12px] text-[11px] uppercase font-semibold transition-all active:scale-[0.97]"
                  style={{
                    letterSpacing: '1px',
                    minWidth: '80px',
                    ...(copied
                      ? { background: '#064e3b', color: '#fff', border: '1px solid #064e3b' }
                      : pill),
                  }}
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </>
            )}
            {navigator.share && (
              <button
                onClick={handleShare}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[12px] text-[11px] uppercase font-semibold transition-all active:scale-[0.97]"
                style={{ letterSpacing: '1px', minWidth: '80px', ...pill }}
              >
                <Share className="w-3.5 h-3.5" />
                Share
              </button>
            )}
            <button
              onClick={handleDelete}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-[12px] text-[11px] uppercase font-semibold transition-all active:scale-[0.97]"
              style={{ letterSpacing: '1px', minWidth: '80px', ...pill }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const portalTarget = typeof document !== 'undefined' ? document.getElementById(PHONE_SCREEN_ID) : null;
  return portalTarget ? createPortal(modalContent, portalTarget) : modalContent;
};

ClipDetailModal.propTypes = {
  clip: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string,
    fileData: PropTypes.string,
    fileType: PropTypes.string,
    addedAt: PropTypes.string.isRequired,
    folderId: PropTypes.string,
    feedStatus: PropTypes.oneOf(['in_feed', 'not_in_feed']),
    processingLevel: PropTypes.oneOf(['summary', 'key_points', 'detailed', 'full']),
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdateClip: PropTypes.func,
  folders: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })),
};

export default ClipDetailModal;
